import { useEffect, useRef, useState } from 'react';

/** Matches CollapseIn's own duration, which is what these rows collapse with. */
export const LEAVING_ROW_MS = 300;

export interface LeavingRows<T> {
  /** What to render: the current rows, plus the ones on their way out, still in place. */
  rows: T[];
  /** Whether this row is on its way out, and should be rendered collapsed. */
  isLeaving: (row: T) => boolean;
}

/** A row held back, with the place it left from and the id its own removal is keyed on. */
interface Leaving<T> {
  row: T;
  index: number;
  id: number;
}

/**
 * Keeps a row on screen for one collapse after it drops out of `rows`, so a day that is settled leaves by
 * animating rather than vanishing under the cursor.
 *
 * Works on whatever took the row away — a mark, a report filed on it, an undo somewhere else — because it
 * compares the list against itself rather than listening for a cause.
 */
export const useLeavingRows = <T>(
  rows: T[],
  keyOf: (row: T) => string,
  durationMs = LEAVING_ROW_MS,
): LeavingRows<T> => {
  const signature = rows.map(keyOf).join();
  const previous = useRef<T[]>(rows);
  const nextId = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const [seen, setSeen] = useState(signature);
  const [leaving, setLeaving] = useState<Leaving<T>[]>([]);

  // Worked out while rendering, not in an effect: an effect runs after the commit that already dropped the
  // row, so it would unmount and come back collapsed with nothing to transition from.
  if (signature !== seen) {
    const keys = new Set(rows.map(keyOf));
    const gone = previous.current
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => !keys.has(keyOf(row)))
      .map(entry => {
        nextId.current += 1;
        return { ...entry, id: nextId.current };
      });

    setSeen(signature);
    setLeaving(current => [
      // A row that came back cancels its own exit, so an undo puts it straight back rather than after a wait.
      ...current.filter(({ row }) => !keys.has(keyOf(row)) && !gone.some(other => keyOf(other.row) === keyOf(row))),
      ...gone,
    ]);
  }

  useEffect(() => {
    const pending = timers.current;
    leaving.forEach(({ id }) => {
      if (pending.has(id)) return;
      // Keyed on the id rather than the row: a row that leaves twice gets its own timer, so the first one
      // cannot cut the second exit short.
      pending.set(
        id,
        setTimeout(() => {
          pending.delete(id);
          setLeaving(current => current.filter(other => other.id !== id));
        }, durationMs),
      );
    });
  }, [leaving, durationMs]);

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach(clearTimeout);
  }, []);

  const keys = new Set(rows.map(keyOf));
  const stillLeaving = leaving.filter(({ row }) => !keys.has(keyOf(row)));

  const merged = [...rows];
  stillLeaving.forEach(({ row, index }) => merged.splice(Math.min(index, merged.length), 0, row));

  const leavingKeys = new Set(stillLeaving.map(({ row }) => keyOf(row)));

  // Every render, not only when the keys move: a row whose content changed while listed must leave carrying
  // what it last showed, not what it showed when the list last gained or lost a row.
  previous.current = rows;

  return { rows: merged, isLeaving: row => leavingKeys.has(keyOf(row)) };
};
